/**
 * Tabs widget — auto-inits any element with [data-tabs-widget]
 */

/**
 * @param {HTMLElement} panel
 */
function reinitEmblaInPanel(panel) {
  requestAnimationFrame(() => {
    let needsInit = false;

    panel.querySelectorAll('.embla-component-slider, .image-slider, .sub-collection-wrapper').forEach((slider) => {
      // @ts-ignore - Set in component-embla.js
      if (slider.emblaSlider && typeof slider.emblaSlider.reInit === 'function') {
        // @ts-ignore
        slider.emblaSlider.reInit();
        return;
      }

      needsInit = true;
    });

    if (needsInit) {
      document.dispatchEvent(new CustomEvent('theme:embla:reinit', { detail: { scope: panel } }));
    }
  });
}

/**
 * @param {HTMLElement} root
 * @param {string} panelId
 */
function activateTabsPanel(root, panelId) {
  const tabs = root.querySelectorAll('.tabs-widget__tab[role="tab"]');
  const panels = root.querySelectorAll('.tabs-widget__panel[role="tabpanel"]');
  if (!tabs.length || !panels.length || !panelId) return;

  const panel = root.querySelector(`#${CSS.escape(panelId)}`);
  if (!panel) return;

  tabs.forEach((tab) => {
    if (!(tab instanceof HTMLButtonElement)) return;

    const isSelected = tab.getAttribute('aria-controls') === panelId;
    tab.setAttribute('aria-selected', isSelected ? 'true' : 'false');
    tab.tabIndex = isSelected ? 0 : -1;
  });

  panels.forEach((item) => {
    const isActive = item.id === panelId;
    item.toggleAttribute('hidden', !isActive);
    item.classList.toggle('tabs-widget__panel--active', isActive);
    if (isActive && item instanceof HTMLElement) {
      reinitEmblaInPanel(item);
    }
  });
}

/**
 * @param {HTMLElement} root
 */
function initTabsWidget(root) {
  if (!root || root.dataset.tabsWidgetInit === 'true') return;

  const tabs = root.querySelectorAll('.tabs-widget__tab[role="tab"]');
  const panels = root.querySelectorAll('.tabs-widget__panel[role="tabpanel"]');
  if (!tabs.length || !panels.length) return;

  root.dataset.tabsWidgetInit = 'true';

  root.addEventListener('click', (event) => {
    if (!(event.target instanceof Element)) return;

    const tab = event.target.closest('.tabs-widget__tab[role="tab"]');
    if (!(tab instanceof HTMLButtonElement) || tab.disabled || !root.contains(tab)) return;

    event.preventDefault();

    const panelId = tab.getAttribute('aria-controls');
    if (!panelId) return;

    activateTabsPanel(root, panelId);
  });

  root.addEventListener('keydown', (event) => {
    const current = root.querySelector('.tabs-widget__tab[aria-selected="true"]');
    if (!(current instanceof HTMLButtonElement)) return;

    const tabList = /** @type {HTMLButtonElement[]} */ (
      Array.from(tabs).filter((tab) => tab instanceof HTMLButtonElement)
    );
    const index = tabList.indexOf(current);
    let next = index;

    if (event.key === 'ArrowRight') {
      next = (index + 1) % tabList.length;
    } else if (event.key === 'ArrowLeft') {
      next = (index - 1 + tabList.length) % tabList.length;
    } else {
      return;
    }

    event.preventDefault();

    const target = tabList[next];
    if (!target || target.disabled) return;

    target.focus();
    const panelId = target.getAttribute('aria-controls');
    if (panelId) activateTabsPanel(root, panelId);
  });
}

/**
 * @param {ParentNode} [scope]
 */
function initAllTabsWidgets(scope = document) {
  scope.querySelectorAll('[data-tabs-widget]').forEach((root) => {
    if (root instanceof HTMLElement) initTabsWidget(root);
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => initAllTabsWidgets());
} else {
  initAllTabsWidgets();
}

document.addEventListener('shopify:section:load', (event) => {
  if (event.target instanceof HTMLElement) {
    initAllTabsWidgets(event.target);
  }
});

/**
 * Theme editor: reveal the tab panel that contains the selected block.
 * @param {Event} event
 */
function handleThemeEditorBlockSelect(event) {
  if (!window.Shopify?.designMode) return;
  if (!(event.target instanceof HTMLElement)) return;

  const panel = event.target.closest('.tabs-widget__panel[role="tabpanel"]');
  if (!panel || !panel.id) return;

  const tabsWidget = panel.closest('[data-tabs-widget]');
  if (!(tabsWidget instanceof HTMLElement)) return;

  initTabsWidget(tabsWidget);
  activateTabsPanel(tabsWidget, panel.id);
}

document.addEventListener('shopify:block:select', handleThemeEditorBlockSelect, true);
